import {Component, computed, effect, ElementRef, OnDestroy, output, viewChild} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {BasicTaDetails, UserService} from '../../../../api';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {DatePipe, NgClass} from '@angular/common';
import {canCreateTemplateFrom, toTAStatusText} from '../../../../util/app.types';
import {Chip} from 'primeng/chip';
import {LocationService} from '../../../../services/location.service';
import {InputText} from 'primeng/inputtext';
import {ScrollHeightDirective} from '../../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';

@Component({
  selector: 'my-history-list',
  imports: [
    TaCommentCount,
    Button,
    Chip,
    DatePipe,
    InputText,
    ReactiveFormsModule,
    TableModule,
    FormsModule,
    NgClass,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
  ],
  templateUrl: './my-history-list.component.html',
  styleUrl: './my-history-list.component.scss'
})
export class MyHistoryListComponent extends TaListBase implements OnDestroy {
  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;
  private loadingDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  createTemplate = output<BasicTaDetails>();

  isLoading = computed(() => this.store.isLoadingData());
  canLoadMore = computed(() => {
    const cache = this.store.historyTaListCache();
    if (!cache || !cache.totalCount) return false;
    return (cache.currentCount ?? 0) < cache.totalCount;
  });

  constructor(
    private userApi: UserService,
    protected locationService: LocationService,
  ) {
    super();

    effect(() => {
      this.availableTaList.set(this.store.historyTaListCache()?.taList ?? []);
    });

    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });

    effect(() => {
      const loading = this.isLoading();
      if (loading) {
        this.loadingDebounceTimer = setTimeout(() => {
          this.loadingService.showLoading({message: 'Loading...'});
        }, 80);
      } else {
        clearTimeout(this.loadingDebounceTimer);
        this.loadingDebounceTimer = undefined;
        this.loadingService.hideLoading();
      }
    });
  }

  // The loader decides whether to refetch - it reuses a cache newer than LIST_CACHE_TTL_MS.
  override ngOnInit() {
    super.ngOnInit();
    this.taSearchDatasource.loadHistoryTaList();
  }

  reloadTaList(): void {
    this.store.resetTaState();
    this.store.resetHistoryCache();
    this.taSearchDatasource.loadHistoryTaList();
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isLoading()) return;
    this.taSearchDatasource.loadMoreHistoryTas();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.observer?.disconnect();
  }

  protected readonly toTAStatusText = toTAStatusText;
  protected readonly canCreateTemplateFrom = canCreateTemplateFrom;
}
